<script setup lang="ts">
import { computed } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { isRecord, toNullableBoolean, toNullableNumber, toNullableString } from '@/utils/coerce';
import { formatDate } from '@/utils/formatters';
import { getRiskLevelDisplay, toTitleCase } from '@/utils/kyc';
import type { KycCategory } from '@/types/kyc';

interface NationalSanctionRecord {
  sanctionList: string | null;
  creationDate: string | null;
  endDate: string | null;
  sanctionName: string | null;
  infraction: string | null;
  sanctionTypeStandard: string | null;
  sanctionsEndYearsSince: number | null;
  isActive: boolean;
  levelValue: number | null;
  ruleId: string | null;
}

interface Props {
  category: KycCategory;
}

const props = defineProps<Props>();

const LIST_LABEL_OVERRIDES: Record<string, string> = {
  ceis: 'CEIS',
  ceipm: 'CEIPM',
  ceaf: 'CEAF',
  ibama: 'IBAMA',
  supervisao: 'Supervisão',
  'banco central do brasil': 'Banco Central do Brasil',
  'tribunal de contas da união': 'Tribunal de Contas da União',
  'conselho nacional de justiça': 'Conselho Nacional de Justiça',
  'bank hindered suppliers': 'Fornecedores Impedidos do Banco',
};

const unwrapRecord = (item: Record<string, unknown>): Record<string, unknown> => {
  if (isRecord(item._custom) && isRecord(item._custom.value)) {
    return item._custom.value;
  }
  return item;
};

const normalizeOptionalDate = (value: unknown): string | null => {
  const dateValue = toNullableString(value)?.trim();
  if (!dateValue) return null;

  const normalizedDate = dateValue
    .replace(/\s+/g, '')
    .replace(/T.*$/, '');

  if (
    normalizedDate === '31/12/9999'
    || normalizedDate === '9999-12-31'
  ) {
    return null;
  }

  return dateValue;
};

const normalizeRecord = (item: Record<string, unknown>): NationalSanctionRecord => ({
  sanctionList: toNullableString(item.sanction_list),
  creationDate: normalizeOptionalDate(item.creation_date),
  endDate: normalizeOptionalDate(item.end_date),
  sanctionName: toNullableString(item.sanction_name),
  infraction: toNullableString(item.infraction),
  sanctionTypeStandard: toNullableString(item.sanction_type_standard),
  sanctionsEndYearsSince: toNullableNumber(item.sanctions_end_years_since),
  isActive: toNullableBoolean(item.is_active) ?? false,
  levelValue: toNullableNumber(item.level_value),
  ruleId: toNullableString(item.rule_id),
});

const sanctions = computed<NationalSanctionRecord[]>(() => {
  const raw = props.category.data_json;
  const items = Array.isArray(raw)
    ? raw
    : isRecord(raw)
      ? raw.national_sanctions ?? raw.items ?? raw.sanctions
      : [];

  if (!Array.isArray(items)) return [];

  return items
    .filter(isRecord)
    .map(unwrapRecord)
    .filter(isRecord)
    .map(normalizeRecord)
    .sort((left, right) => {
      if (left.isActive !== right.isActive) return left.isActive ? -1 : 1;
      return (right.levelValue ?? -1) - (left.levelValue ?? -1);
    });
});

const activeSanctionsCount = computed(() => sanctions.value.filter((sanction) => sanction.isActive).length);

const uniqueListsCount = computed(() => (
  new Set(
    sanctions.value
      .map((sanction) => sanction.sanctionList?.trim().toLocaleLowerCase('pt-BR'))
      .filter((value): value is string => Boolean(value))
  ).size
));

const formatListLabel = (value: string | null): string => {
  if (!value) return 'Lista não informada';
  const normalized = value.trim().toLocaleLowerCase('pt-BR');
  return LIST_LABEL_OVERRIDES[normalized] ?? toTitleCase(normalized);
};

const formatSanctionTitle = (record: NationalSanctionRecord): string => (
  record.sanctionName
  || record.infraction
  || record.sanctionTypeStandard
  || 'Sanção não detalhada'
);

const formatSanctionSubtitle = (record: NationalSanctionRecord): string | null => {
  if (record.sanctionName && record.infraction) return record.infraction;
  if (record.sanctionTypeStandard && record.sanctionTypeStandard !== record.sanctionName) return record.sanctionTypeStandard;
  return null;
};

const getRiskTagAttrs = (levelValue: number | null) => {
  const display = getRiskLevelDisplay(levelValue);
  return {
    value: display.label,
    style: {
      backgroundColor: display.hexBgColor,
      color: display.hexColor,
    },
    class: [display.borderClass, 'border'],
  };
};

const formatRelativeEndAge = (value: number | null): string | null => {
  if (value === null) return null;
  const rounded = Number(value.toFixed(1));
  return rounded === 1 ? 'há 1 ano' : `há ${rounded.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} anos`;
};
</script>

<template>
  <div class="mt-3 space-y-4">
    <div
      v-if="sanctions.length"
      class="grid grid-cols-1 gap-3 md:grid-cols-3"
    >
      <div class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Ocorrências</div>
        <div class="mt-2 flex items-center gap-2">
          <BaseIcon class="text-xl text-slate-500">gavel</BaseIcon>
          <span class="text-2xl font-semibold text-slate-900">{{ sanctions.length }}</span>
        </div>
      </div>

      <div class="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Sanções vigentes</div>
        <div class="mt-2 flex items-center gap-2">
          <BaseIcon class="text-xl text-amber-700">warning</BaseIcon>
          <span class="text-2xl font-semibold text-amber-900">{{ activeSanctionsCount }}</span>
        </div>
      </div>

      <div class="rounded-xl border border-sky-200 bg-sky-50 p-4 shadow-sm">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-sky-700">Listas distintas</div>
        <div class="mt-2 flex items-center gap-2">
          <BaseIcon class="text-xl text-sky-700">format_list_bulleted</BaseIcon>
          <span class="text-2xl font-semibold text-sky-900">{{ uniqueListsCount }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="sanctions.length"
      class="max-h-[min(28vh,36rem)] overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200 bg-white overscroll-y-contain"
    >
      <DataTable
        :value="sanctions"
        responsiveLayout="scroll"
        stripedRows
        class="p-datatable-sm text-sm"
      >
        <Column header="Risco">
          <template #body="{ data }: { data: NationalSanctionRecord }">
            <Tag v-bind="getRiskTagAttrs(data.levelValue)" />
          </template>
        </Column>

        <Column header="Lista">
          <template #body="{ data }: { data: NationalSanctionRecord }">
            <div class="font-medium text-slate-900">{{ formatListLabel(data.sanctionList) }}</div>
            <div v-if="data.ruleId" class="text-[11px] text-slate-500">
              Regra <span class="font-mono">{{ data.ruleId.slice(0, 8) }}</span>
            </div>
          </template>
        </Column>

        <Column header="Ocorrência">
          <template #body="{ data }: { data: NationalSanctionRecord }">
            <div class="font-medium text-slate-900">{{ formatSanctionTitle(data) }}</div>
            <div v-if="formatSanctionSubtitle(data)" class="text-xs text-slate-500">
              {{ formatSanctionSubtitle(data) }}
            </div>
          </template>
        </Column>

        <Column header="Situação">
          <template #body="{ data }: { data: NationalSanctionRecord }">
            <div class="flex flex-wrap items-center gap-1.5">
              <Tag :value="data.isActive ? 'Vigente' : 'Expirada'" :severity="data.isActive ? 'danger' : 'secondary'" />
            </div>
            <div class="mt-1 text-xs text-slate-500">
              Inclusão: {{ formatDate(data.creationDate, undefined, 'Data não informada') }}
            </div>
            <div class="text-xs text-slate-500">
              Fim: {{ formatDate(data.endDate, undefined, data.isActive ? 'Ainda vigente' : 'Não informado') }}
              <span v-if="!data.isActive && formatRelativeEndAge(data.sanctionsEndYearsSince)">
                ({{ formatRelativeEndAge(data.sanctionsEndYearsSince) }})
              </span>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <div v-else class="text-sm italic text-slate-500">
      Nenhuma sanção nacional encontrada.
    </div>
  </div>
</template>
